/**
 * FindingKicker — the state chips that anchor a finding: severity, type,
 * and (when not plain open) its lifecycle state. Extra chips for the caller's
 * context ("Seen 3×", persona) go in `children`, after the state.
 *
 * Severity and type carry their tone (critical → red, important → amber,
 * strength → green); status chips are neutral except "Fixed".
 */

import type { ReactNode } from "react";
import { Chip, type ChipTone } from "@/components/ui/chip";
import { cn } from "@/lib/utils";
import {
  findingTypeLabel,
  severityLabel,
  type FindingType,
  type Severity,
} from "./vocabulary";
import {
  canonicalStateChip,
  normalizeCanonicalStatus,
  type CanonicalStatus,
} from "@/components/finding-badges";

type FixStatus =
  | "open"
  | "fix_proposed"
  | "fix_applied"
  | "verified_resolved"
  | "verified_persists"
  | "wont_fix";

interface FindingKickerProps {
  severity?: Severity | null;
  type?: FindingType | null;
  /**
   * Canonical lifecycle state (`canonical_findings.state`). When given it
   * replaces the legacy `status` chip.
   */
  state?: string | null;
  /** `state_reason` — names the dismissal or why it reopened. */
  stateReason?: string | null;
  /** Legacy canonical status (open/addressed/dismissed) */
  status?: CanonicalStatus | string | null;
  /** Fix-lifecycle status — rendered when `status` is falsy or "open" */
  fixStatus?: FixStatus | string | null;
  /** Extra context chips rendered after the state chips */
  children?: ReactNode;
  /** Chips rendered before severity — the queue puts priority first. */
  leading?: ReactNode;
  className?: string;
}

const severityChipTone: Record<Severity, ChipTone> = {
  critical: "critical",
  major: "important",
  minor: "neutral",
  nit: "muted",
};

const typeChipTone: Record<FindingType, ChipTone> = {
  hinders: "critical",
  missing: "important",
  incorrect: "important",
  helps: "helps",
  irrelevant: "muted",
};

const fixStatusChip: Record<
  Exclude<FixStatus, "open">,
  { label: string; tone: ChipTone }
> = {
  fix_proposed: { label: "Fix proposed", tone: "primary" },
  fix_applied: { label: "Fix applied", tone: "primary" },
  verified_resolved: { label: "Resolved", tone: "helps" },
  verified_persists: { label: "Persists", tone: "critical" },
  wont_fix: { label: "Won't fix", tone: "muted" },
};

export function FindingKicker({
  severity,
  type,
  state,
  stateReason,
  status,
  fixStatus,
  children,
  leading,
  className,
}: FindingKickerProps) {
  const stateChip = state ? canonicalStateChip(state, stateReason) : null;
  const normalizedStatus =
    !state && status ? normalizeCanonicalStatus(status) : null;

  const showStatus = normalizedStatus && normalizedStatus !== "open";
  // fix_status is legacy; a lifecycle state supersedes it.
  const fixChip =
    !state && !showStatus && fixStatus && fixStatus !== "open"
      ? fixStatusChip[fixStatus as Exclude<FixStatus, "open">]
      : undefined;

  // Severity is a problem-severity scale; a "minor strength" or
  // "important strength" reads awkwardly. Suppress severity for helps.
  const showSeverity = severity && type !== "helps";
  const showType = Boolean(type);

  // When severity is shown beside type, type gets a muted tone so severity
  // carries the primary color signal without noisy double-coloring.
  const resolvedTypeTone: ChipTone =
    type === "helps"
      ? "helps"
      : showSeverity
        ? "muted"
        : type && typeChipTone[type as FindingType]
          ? typeChipTone[type as FindingType]
          : "muted";

  return (
    <div className={cn("flex flex-wrap items-center gap-1.5", className)}>
      {leading}
      {showSeverity && severity && (
        <Chip tone={severityChipTone[severity]}>{severityLabel(severity)}</Chip>
      )}
      {showType && type && (
        <Chip tone={resolvedTypeTone}>{findingTypeLabel(type)}</Chip>
      )}
      {showStatus && (
        <Chip tone={normalizedStatus === "addressed" ? "helps" : "muted"}>
          {normalizedStatus === "addressed" ? "Fixed" : "Dismissed"}
        </Chip>
      )}
      {stateChip && <Chip tone={stateChip.tone}>{stateChip.label}</Chip>}
      {fixChip && <Chip tone={fixChip.tone}>{fixChip.label}</Chip>}
      {children}
    </div>
  );
}
